import React, { useRef, useEffect, useId, type ReactNode, useState, forwardRef } from 'react';
import { styled, css } from '@compiled/react';
import { layers } from '@jira/platform__ui-layers';
import { useThemeObserver, token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';

let namespaceUUID = 0;

const newPalette = {
	dark: {
		blue: '#0468FF',
		teal: '#BF63F3',
		yellow: '#FFA900',
	},
	light: {
		blue: '#0468FF',
		teal: '#BF63F3',
		yellow: '#FFA900',
	},
};

const AFTER_LOAD_DURATION = 3000;

const useNamespaceId = () => {
	if (fg('jfp_magma_jira-namespaceId-svg')) {
		// eslint-disable-next-line react-hooks/react-compiler,react-hooks/rules-of-hooks
		return useId();
	}
	// eslint-disable-next-line react-hooks/react-compiler,react-hooks/rules-of-hooks
	const namespaceId = useRef<number>();
	if (namespaceId.current === undefined) {
		namespaceId.current = namespaceUUID;

		namespaceUUID += 1;
	}
	return namespaceId.current;
};
const AnimatedSvgContainer = ({
	isLoading,
	isBlurred,
	isShadow,
	isBorderGrey,
	hasNewBorderExperience,
	components: { SvgComponent = SvgContainer } = {},
}: {
	isLoading?: boolean;
	isBlurred?: boolean;
	isShadow?: boolean;
	isBorderGrey?: boolean;
	hasNewBorderExperience?: boolean;
	components?: {
		SvgComponent?: React.ComponentType<React.ComponentProps<typeof SvgContainer>>;
	};
}) => {
	const { colorMode } = useThemeObserver();
	const svgRef = useRef<SVGSVGElement>(null);
	const namespaceId = useNamespaceId();
	const [showRainbowBorder, setShowRainbowBorder] = useState(false);
	const paletteForTheme = colorMode === 'dark' ? newPalette.dark : newPalette.light;

	useEffect(() => {
		if (isLoading) {
			const svg = svgRef.current;
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
			hasNewBorderExperience && setShowRainbowBorder(true);
			// Schedule animation to begin before next browser paint
			const beginReq = requestAnimationFrame(() => {
				svg?.querySelectorAll('animate').forEach((node) => node.beginElement());
			});
			return () => {
				// Ensure any pending animation frame is cancelled as the element animation does not end properly if it
				// begins in the same event tick, i.e. isLoading synchronously changes to true then false.
				cancelAnimationFrame(beginReq);
				requestAnimationFrame(() => {
					svg?.querySelectorAll('animate').forEach((node) => node.endElement());
				});
			};
		}

		return undefined;
	}, [isLoading, hasNewBorderExperience]);

	// eslint-disable-next-line react-you-might-not-need-an-effect/no-reset-all-state-on-prop-change
	useEffect(() => {
		// we keep the rainbow border without animations for at 3 seconds after load. After that we show the grey border
		if (hasNewBorderExperience && !isLoading && showRainbowBorder) {
			const timeoutId = setTimeout(() => setShowRainbowBorder(false), AFTER_LOAD_DURATION);
			return () => clearTimeout(timeoutId);
		}
	}, [hasNewBorderExperience, showRainbowBorder, isLoading]);

	return (
		<SvgComponent
			isBlurred={isBlurred}
			isShadow={isShadow}
			ref={svgRef}
			viewBox="0 0 24 24"
			preserveAspectRatio="none"
			aria-hidden="true"
		>
			<defs>
				<linearGradient
					id={`${namespaceId}_lg1`}
					gradientUnits="userSpaceOnUse"
					x1="0%"
					y1="0"
					x2="400%"
					y2="0"
					spreadMethod="reflect"
				>
					<animate
						begin="indefinite"
						attributeName="x1"
						from="0%"
						to="400%"
						repeatCount="indefinite"
						dur="3s"
					/>
					<animate
						begin="indefinite"
						attributeName="x2"
						from="400%"
						to="800%"
						repeatCount="indefinite"
						dur="3s"
					/>
					<stop offset="0" stopColor={paletteForTheme.blue} />
					<stop offset="0.25" stopColor={paletteForTheme.teal} />
					<stop offset="0.5" stopColor={paletteForTheme.yellow} />
					<stop offset="0.75" stopColor={paletteForTheme.teal} />
					<stop offset="1" stopColor={paletteForTheme.blue} />
				</linearGradient>
				<linearGradient
					id={`${namespaceId}_lg2`}
					gradientUnits="userSpaceOnUse"
					x1="0"
					y1="-100%"
					x2="0"
					y2="300%"
					spreadMethod="reflect"
				>
					<animate
						begin="indefinite"
						attributeName="y1"
						from="-100%"
						to="300%"
						repeatCount="indefinite"
						dur="3s"
					/>
					<animate
						begin="indefinite"
						attributeName="y2"
						from="300%"
						to="700%"
						repeatCount="indefinite"
						dur="3s"
					/>
					<stop offset="0" stopColor={paletteForTheme.blue} />
					<stop offset="0.25" stopColor={paletteForTheme.teal} />
					<stop offset="0.5" stopColor={paletteForTheme.yellow} />
					<stop offset="0.75" stopColor={paletteForTheme.teal} />
					<stop offset="1" stopColor={paletteForTheme.blue} />
				</linearGradient>
				<linearGradient
					id={`${namespaceId}_lg3`}
					gradientUnits="userSpaceOnUse"
					x1="300%"
					y1="0"
					x2="700%"
					y2="0"
					spreadMethod="reflect"
				>
					<animate
						begin="indefinite"
						attributeName="x1"
						from="300%"
						to="-100%"
						repeatCount="indefinite"
						dur="3s"
					/>
					<animate
						begin="indefinite"
						attributeName="x2"
						from="700%"
						to="300%"
						repeatCount="indefinite"
						dur="3s"
					/>
					<>
						<stop offset="0" stopColor={paletteForTheme.blue} />
						<stop offset="0.25" stopColor={paletteForTheme.blue} />
						<stop offset="0.5" stopColor={paletteForTheme.yellow} />
						<stop offset="0.75" stopColor={paletteForTheme.blue} />
						<stop offset="1" stopColor={paletteForTheme.blue} />
					</>
				</linearGradient>

				<linearGradient
					id={`${namespaceId}_lg4`}
					gradientUnits="userSpaceOnUse"
					x1="0"
					y1="0"
					x2="0"
					y2="400%"
					spreadMethod="reflect"
				>
					<animate
						begin="indefinite"
						attributeName="y1"
						from="0%"
						to="-400%"
						repeatCount="indefinite"
						dur="3s"
					/>
					<animate
						begin="indefinite"
						attributeName="y2"
						from="400%"
						to="0%"
						repeatCount="indefinite"
						dur="3s"
					/>
					<stop offset="0" stopColor={paletteForTheme.blue} />
					<stop offset="0.25" stopColor={paletteForTheme.blue} />
					<stop offset="0.5" stopColor={paletteForTheme.yellow} />
					<stop offset="0.75" stopColor={paletteForTheme.teal} />
					<stop offset="1" stopColor={paletteForTheme.blue} />
				</linearGradient>
			</defs>

			{isBorderGrey || (hasNewBorderExperience && !showRainbowBorder && !isLoading) ? (
				<>
					<g strokeWidth="16">
						<path
							stroke={token('color.background.accent.gray.subtler')}
							d="M0 0h24v24H0Z"
							vectorEffect="non-scaling-stroke"
							fill="none"
						/>
					</g>
				</>
			) : (
				<>
					<g strokeWidth="16">
						<path
							stroke={`url(#${namespaceId}_lg1)`}
							d="M0 0h24"
							vectorEffect="non-scaling-stroke"
						/>
						<path
							stroke={`url(#${namespaceId}_lg2)`}
							d="M24 0v24"
							vectorEffect="non-scaling-stroke"
						/>
						<path
							stroke={`url(#${namespaceId}_lg3)`}
							d="M24 24H0"
							vectorEffect="non-scaling-stroke"
						/>
						<path
							stroke={`url(#${namespaceId}_lg4)`}
							d="M0 24V0"
							vectorEffect="non-scaling-stroke"
						/>
					</g>
				</>
			)}
		</SvgComponent>
	);
};

type Props = {
	children: ReactNode;
	isLoading?: boolean;
	isShadow?: boolean;
	shouldFitContainer?: boolean;
	spacing?: 'default' | 'none';
	testId?: string;
	isBorderGrey?: boolean;
	components?: {
		ContainerComponent?:
			| React.ComponentType<React.ComponentProps<typeof Container>>
			| React.FC<{
					children: ReactNode;
					testId?: string;
					zIndex?: number;
					shouldFitContainer: boolean;
			  }>;
		ContentComponent?: React.ComponentType<React.ComponentProps<typeof Content>>;
		SvgComponent?: React.ComponentType<React.ComponentProps<typeof SvgContainer>>;
	};
	hasNewBorderExperience?: boolean;
	borderless?: boolean;
	zIndex?: number;
};

const AIContainer = forwardRef<HTMLDivElement, Props>(
	(
		{
			children,
			isLoading,
			isShadow = false,
			shouldFitContainer = false,
			spacing = 'default',
			testId,
			isBorderGrey,
			components: {
				ContainerComponent = Container,
				ContentComponent = Content,
				SvgComponent = SvgContainer,
			} = {},
			hasNewBorderExperience = false,
			borderless = false,
			zIndex = layers.card,
		},
		ref,
	) => {
		return (
			<ContainerComponent
				data-testid={testId}
				testId={testId}
				shouldFitContainer={shouldFitContainer}
				zIndex={zIndex}
			>
				{(!fg('similar_items_in_gic_ui_fixes') || !borderless) && (
					<AnimatedSvgContainer
						isShadow={isShadow}
						isLoading={isLoading}
						isBorderGrey={isBorderGrey}
						hasNewBorderExperience={hasNewBorderExperience}
						components={{
							SvgComponent,
						}}
					/>
				)}
				{isLoading && <AnimatedSvgContainer isLoading={isLoading} isBlurred />}
				<ContentComponent ref={ref} spacing={spacing}>
					{children}
				</ContentComponent>
			</ContainerComponent>
		);
	},
);

export default AIContainer;

AIContainer.displayName = 'AIContainer';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{ shouldFitContainer: boolean; zIndex?: number }>({
	display: 'flex',
	position: 'relative',
	paddingTop: token('space.025'),
	paddingRight: token('space.025'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.025'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: (props) => (props.shouldFitContainer ? 'auto' : 'fit-content'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	zIndex: (props) => props.zIndex || layers.card,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Content = styled.div<{ spacing: 'default' | 'none'; zIndex?: number }>(
	{
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.medium'),
		flexGrow: 1,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		zIndex: (props) => props.zIndex || layers.card,
		width: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.spacing === 'default'
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({
					paddingTop: token('space.100'),
					paddingRight: token('space.100'),
					paddingBottom: token('space.100'),
					paddingLeft: token('space.100'),
				})
			: undefined,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SvgContainer = styled.svg<{
	isBlurred?: boolean;
	isShadow?: boolean;
}>(
	{
		position: 'absolute',
		borderRadius: token('radius.large'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		boxShadow: (props) => (props.isShadow ? token('elevation.shadow.overflow') : 'none'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.isBlurred
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({
					width: 'calc(100% - 16px)',
					height: 'calc(100% - 16px)',
					top: token('space.100'),
					left: token('space.100'),
					filter: 'blur(16px)',
				})
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({
					left: 0,
					top: 0,
					width: '100%',
					height: '100%',
				}),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const defaultComponents: Required<NonNullable<Props['components']>> = {
	ContainerComponent: Container,
	ContentComponent: Content,
	SvgComponent: SvgContainer,
};
